import type {
  LocalPublication,
  PublicationRefusal,
  PublicationSendPolicy,
  PublicationState,
} from '@streamlain/doc-publication-contract';
import { type DBSchema, type IDBPDatabase, type OpenDBCallbacks } from 'idb';

import type {
  LocalDataContainer,
  LocalDocKeyRecord,
  LocalIndexRow,
  LocalIndexState,
} from '../../local-data/payload';
import type { DocClock } from '../../storage/doc';
import type { JournaledPublication } from '../../storage/doc-publication';
import type { LocalAuthState } from '../../storage/local-data-access';

interface IDBPublicationReceiptRecord {
  operationId: string;
  sequence: number;
  mutationId: string;
  clocks: readonly DocClock[];
}

export type IDBPublicationRecord = IDBPublicationReceiptRecord & {
  publication?: JournaledPublication;
  encrypted?: {
    header: LocalDataContainer;
    members: (Omit<LocalPublication['members'][number], 'update'> & {
      payload: LocalDataContainer;
    })[];
    /** Absent in entries of the first journal tag. */
    companions?: { docId: string; payload: LocalDataContainer }[];
  };
  storageRevision?: string;
  keyScopeId?: string[];
  state: 'LOCAL_COMMITTED' | 'PENDING_REMOTE' | 'REBASING' | 'BLOCKED_CONFLICT';
  /** Why the server refused it; only while BLOCKED_CONFLICT. */
  refusal?: PublicationRefusal;
  attempt: number;
  send?: PublicationSendPolicy | null;
  docIds: string[];
};

/**
IndexedDB
  > DB(workspace:${workspaceId})
     > Table(Snapshots)
     > Table(Updates)
     > Table(...)

Table(Snapshots)
| docId | blob | createdAt | updatedAt |
|-------|------|-----------|-----------|
|  str  | bin  |   Date    |   Date    |

Table(Updates)
| id | docId | blob | createdAt |
|----|-------|------|-----------|
|auto|  str  | bin  |   Date    |

Table(Clocks)
| docId |   clock   |
|-------|-----------|
|  str  |   Date    |

Table(Blobs)
| key | mime | size | createdAt | deletedAt |
|-----|------|------|-----------|-----------|
| str |  str | num  |   Date    |   Date    |

Table(BlobData)
| key | data |
|-----|------|
| str | bin  |

Table(PeerClocks)
| peer | docId |   clock   |  pushed   |
|------|-------|-----------|-----------|
| str  |  str  |   Date    |   Date    |

Table(IndexerSync)
| docId | indexedClock | indexerVersion |
|-------|--------------|----------------|
| str   |   Date       |    number      |

Table(BlobSync)
| peer | key | uploadedAt |
|------|-----|------------|
| str  | str |   Date     |
 */
export interface DocStorageSchema extends DBSchema {
  docKeyRegistry: {
    key: [string, string, string];
    value: LocalDocKeyRecord;
    indexes: { keyScopeId: string };
  };
  authState: {
    key: string;
    value: LocalAuthState & { accountId: string };
  };
  publications: {
    key: string;
    value: IDBPublicationRecord;
    indexes: {
      sequence: number;
      docIds: string;
      state: PublicationState;
      keyScopeId: string;
    };
  };
  publicationMeta: {
    key: string;
    value: { key: 'sequence' | 'recoveryVersion'; value: number };
  };
  publicationActors: {
    key: string;
    value: { docId: string; clientId: number; nextClock: number };
  };
  remotePublicationMarks: {
    key: [string, string];
    value: {
      peer: string;
      docId: string;
      viewId: string;
      snapshotId: string | null;
      lastSeq: number;
      contentHash: string | null;
    };
  };
  snapshots: {
    key: string;
    value: {
      docId: string;
      bin?: Uint8Array;
      payload?: LocalDataContainer;
      keyScopeId?: string;
      storageRevision?: string;
      createdAt: Date;
      updatedAt: Date;
      /** SHA256 checksum for integrity verification (optional for backwards compat) */
      checksum?: string;
    };
    indexes: {
      updatedAt: Date;
      keyScopeId: string;
    };
  };
  updates: {
    key: [string, Date];
    value: {
      docId: string;
      bin?: Uint8Array;
      payload?: LocalDataContainer;
      keyScopeId?: string;
      storageRevision?: string;
      createdAt: Date;
      /** SHA256 checksum for integrity verification (optional for backwards compat) */
      checksum?: string;
    };
    indexes: {
      docId: string;
      keyScopeId: string;
    };
  };
  clocks: {
    key: string;
    value: {
      docId: string;
      timestamp: Date;
    };
    indexes: {
      timestamp: Date;
    };
  };
  blobs: {
    key: string;
    value: {
      key: string;
      mime?: string;
      size?: number;
      createdAt?: Date;
      payload?: LocalDataContainer;
      keyScopeId?: string;
      storageRevision?: string;
      deletedAt: Date | null;
    };
    indexes: { keyScopeId: string };
  };
  blobSync: {
    key: [string, string];
    value: {
      peer: string;
      key: string;
      uploadedAt: Date | null;
    };
    indexes: {
      peer: string;
    };
  };
  blobData: {
    key: string;
    value: {
      key: string;
      data?: Uint8Array;
      payload?: LocalDataContainer;
      keyScopeId?: string;
      storageRevision?: string;
    };
    indexes: { keyScopeId: string };
  };
  peerClocks: {
    key: [string, string];
    value: {
      peer: string;
      docId: string;
      clock: Date;
      pulledClock: Date;
      pushedClock: Date;
    };
    indexes: {
      peer: string;
    };
  };
  indexerSync: {
    key: string;
    value: {
      docId: string;
      indexedClock: Date;
      indexerVersion?: number;
    };
  };
  indexSegments: {
    key: string;
    value: LocalIndexRow;
    indexes: {
      recordKey: [string, string];
      ownerDocId: string;
      keyScopeId: string;
      nid: number;
    };
  };
  indexSegmentMeta: { key: 'state'; value: LocalIndexState };
  indexerMetadata: {
    key: string;
    value: {
      key: string;
      value: any;
    };
  };
  indexerRecords: {
    key: number;
    value: {
      table: string;
      id: string;
      data: Map<string, string[]>;
    };
    indexes: { table: string; id: [string, string] };
  };
  invertedIndex: {
    key: number;
    value: {
      table: string;
      nid: number;
      pos?: {
        i: number /* index */;
        l: number /* length */;
        rs: [number, number][] /* ranges: [start, end] */;
      }[];
      key: ArrayBuffer;
    };
    indexes: { key: [string, ArrayBuffer]; nid: number };
  };
}

const migrate: OpenDBCallbacks<DocStorageSchema>['upgrade'] = (
  db,
  oldVersion,
  newVersion,
  trx
) => {
  if (!oldVersion) {
    oldVersion = 0;
  }

  // A fixture may stop at an older layout; a newer app's version runs every
  // migration this app knows.
  const target = Math.min(newVersion ?? migrations.length, migrations.length);
  for (let i = oldVersion; i < target; i++) {
    migrations[i](db, trx);
  }
};

type MigrateParameters = Parameters<
  NonNullable<OpenDBCallbacks<DocStorageSchema>['upgrade']>
>;
type Migrate = (db: MigrateParameters[0], trx: MigrateParameters[3]) => void;

// START REGION: migrations
const init: Migrate = db => {
  const snapshots = db.createObjectStore('snapshots', {
    keyPath: 'docId',
    autoIncrement: false,
  });

  snapshots.createIndex('updatedAt', 'updatedAt', { unique: false });

  const updates = db.createObjectStore('updates', {
    keyPath: ['docId', 'createdAt'],
    autoIncrement: false,
  });

  updates.createIndex('docId', 'docId', { unique: false });

  const clocks = db.createObjectStore('clocks', {
    keyPath: 'docId',
    autoIncrement: false,
  });

  clocks.createIndex('timestamp', 'timestamp', { unique: false });

  const peerClocks = db.createObjectStore('peerClocks', {
    keyPath: ['peer', 'docId'],
    autoIncrement: false,
  });

  peerClocks.createIndex('peer', 'peer', { unique: false });

  db.createObjectStore('blobs', {
    keyPath: 'key',
    autoIncrement: false,
  });

  db.createObjectStore('blobData', {
    keyPath: 'key',
    autoIncrement: false,
  });

  // The first layout held locks in the database; dropLockStore removes it.
  (db as unknown as IDBPDatabase).createObjectStore('locks', {
    keyPath: 'key',
    autoIncrement: false,
  });
};
const initBlobSync: Migrate = db => {
  const blobSync = db.createObjectStore('blobSync', {
    keyPath: ['peer', 'key'],
    autoIncrement: false,
  });

  blobSync.createIndex('peer', 'peer', { unique: false });
};
const initIndexer: Migrate = db => {
  db.createObjectStore('indexerMetadata', {
    keyPath: 'key',
  });
  const indexRecordsStore = db.createObjectStore('indexerRecords', {
    autoIncrement: true,
  });
  indexRecordsStore.createIndex('table', 'table', {
    unique: false,
  });
  indexRecordsStore.createIndex('id', ['table', 'id'], {
    unique: true,
  });
  const invertedIndexStore = db.createObjectStore('invertedIndex', {
    autoIncrement: true,
  });
  invertedIndexStore.createIndex('key', ['table', 'key'], {
    unique: false,
  });
  invertedIndexStore.createIndex('nid', 'nid', { unique: false });
  db.createObjectStore('indexerSync', {
    keyPath: 'docId',
    autoIncrement: false,
  });
};
const initLocalPublication: Migrate = db => {
  db.createObjectStore('authState', { keyPath: 'accountId' });
  const publications = db.createObjectStore('publications', {
    keyPath: 'operationId',
  });
  publications.createIndex('sequence', 'sequence', { unique: true });
  publications.createIndex('docIds', 'docIds', { multiEntry: true });
  publications.createIndex('state', 'state');
  db.createObjectStore('publicationMeta', { keyPath: 'key' });
  db.createObjectStore('publicationActors', { keyPath: 'docId' });
  db.createObjectStore('remotePublicationMarks', {
    keyPath: ['peer', 'docId'],
  });
};
const initLocalEncryption: Migrate = (db, tx) => {
  const registry = db.createObjectStore('docKeyRegistry', {
    keyPath: ['accountId', 'workspaceId', 'keyScopeId'],
  });
  registry.createIndex('keyScopeId', 'keyScopeId', { unique: true });
  for (const name of ['updates', 'snapshots', 'blobs', 'blobData'] as const)
    tx.objectStore(name).createIndex('keyScopeId', 'keyScopeId');
  tx.objectStore('publications').createIndex('keyScopeId', 'keyScopeId', {
    multiEntry: true,
  });
};
const initEncryptedIndexSegments: Migrate = db => {
  const segments = db.createObjectStore('indexSegments', {
    keyPath: 'segmentId',
  });
  segments.createIndex('recordKey', 'recordKey', { unique: true });
  segments.createIndex('ownerDocId', 'ownerDocId');
  segments.createIndex('keyScopeId', 'keyScopeId');
  segments.createIndex('nid', 'nid', { unique: true });
  db.createObjectStore('indexSegmentMeta', { keyPath: 'key' });
};
// Locks live in the Web Locks API: a lock row outlived a holder that ended
// mid-lock (a reload) and blocked every later reader of its doc for 30 s.
const dropLockStore: Migrate = db => {
  (db as unknown as IDBPDatabase).deleteObjectStore('locks');
};
// END REGION

// 1. all schema changed should be put in migrations
// 2. order matters
const migrations: Migrate[] = [
  init,
  initBlobSync,
  initIndexer,
  initLocalPublication,
  initLocalEncryption,
  initEncryptedIndexSegments,
  dropLockStore,
];

export const migrator = {
  version: migrations.length,
  migrate,
};
